/**
 * Kando brand tokens - the single source of truth for colour and type.
 *
 * Nothing else in the codebase should hard-code a hex value or a font stack.
 * Components consume these through the MUI theme (`src/theme/theme.ts`), so a
 * rebrand is a change to this file and nothing else.
 */

/**
 * Core palette. Named for what the colour *is*, not where it is used.
 *
 * The brand is yellow-led and deliberately warm. The one rule that matters:
 * **yellow is a surface, never text on a light background.** `sun` on `paper` is
 * 1.31:1 and unreadable. Where an accent needs to be *read* rather than seen, use
 * `ember` - see the contrast notes on each token.
 *
 * The yellow is a pastel by choice. A saturated yellow sat too close to Lift, the
 * South African airline, so `sun` was lightened and desaturated until the two stopped
 * reading as relatives. The cost is separation: a pastel fill on a warm off-white
 * carries less luminance difference than a saturated one, which is why `sunDeep`
 * exists and why filled buttons take a border rather than floating on the page.
 */
export const palette = {
  /** The brand yellow. Large fills, buttons, highlights - always with ink on top. */
  sun: '#FFDA85',
  /** Deeper. Hovers, and the border that gives a sun-filled block its edge. */
  sunDeep: '#F3C45C',
  /** Pale wash for tinted sections. */
  sunPale: '#FFF4DC',

  /** Warm near-black. Body copy, and the text that sits on `sun`. */
  ink: '#1A1712',
  /** Secondary copy. 7.2:1 on `paper` - safe for body text. */
  inkSoft: '#5C554A',

  /** Warm off-white. The resting surface - deliberately not pure white. */
  paper: '#FFFCF4',
  paperSunk: '#FFF8E7',

  /**
   * Burnt orange. The accent that is safe to *read*: 5.2:1 on `paper`.
   * Used for eyebrows, links and single points of emphasis.
   */
  ember: '#B8461A',
  emberBright: '#F4633A',

  /** Dark-scheme surfaces. Yellow sings on these. */
  night: '#17140E',
  nightRaised: '#221E16',
  nightLine: '#393328',

  line: '#EFE6D2',
} as const;

export const fonts = {
  /** Editorial serif - headings and the wordmark. */
  display: "'Newsreader Variable', 'Newsreader', Georgia, 'Times New Roman', serif",
  /** Interface and body copy. */
  body: "'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  /** Eyebrows, figures, and anything that should align in columns. */
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
} as const;

/**
 * Vertical rhythm. Section padding and stack gaps derive from these rather than
 * from ad-hoc values, which is what keeps pages feeling like one system.
 */
export const spacing = {
  /** Padding applied to the top and bottom of every page section. */
  sectionY: { xs: 8, md: 14 },
  /** Maximum line length for readable prose. */
  proseMaxWidth: '68ch',
} as const;

/** Generous curves. A friendly brand should not have sharp corners. */
export const radii = {
  sm: 8,
  md: 14,
  lg: 24,
  /** Buttons and chips are fully rounded. */
  pill: 999,
} as const;
